/**
 * Shopping Cart Component
 *
 * Displays cart items and checkout for DIRECT PURCHASES on ClothedEye.com
 * This is SEPARATE from the Scene Builder Shopping List (which links to Etsy).
 *
 * Visual Distinctions:
 * - Uses shopping_cart icon (vs receipt/list for Scene Builder)
 * - Green "Buy Direct" branding (vs Etsy orange)
 * - Sidebar drawer (vs Scene Builder modal)
 * - Direct checkout with Stripe (vs Etsy redirect)
 */

import React, { useState } from 'react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModals } from './auth/AuthModalManager';
import stripeService from '../services/stripeService';
import MaterialIcon from './MaterialIcon';

// Note: MaterialIcon uses 'icon' prop, not 'name'
import './ShoppingCart.css';

const ShoppingCart = ({ isOpen, onClose }) => {
  const {
    items,
    itemCount,
    subtotal,
    shipping,
    total,
    isEmpty,
    removeItem,
    updateQuantity,
    clearCart,
    validateCart,
    exportForCheckout,
  } = useCart();

  const { isAuthenticated, isSubscriber } = useAuth();
  const { openLogin } = useAuthModals();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Handle checkout - create Stripe checkout session
   */
  const handleCheckout = async () => {
    setError('');

    // Require authentication for checkout
    if (!isAuthenticated) {
      openLogin();
      return;
    }

    // Validate cart
    const validationErrors = validateCart();
    if (validationErrors.length > 0) {
      setError(
        `Please remove subscriber-only items: ${validationErrors.map((e) => e.title).join(', ')}`
      );
      return;
    }

    // Check if cart is empty
    if (isEmpty) {
      setError('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      // Get auth token
      const token = localStorage.getItem('ce_auth_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Export cart data for checkout
      const cartData = exportForCheckout();

      // Create Stripe checkout session
      const result = await stripeService.createPurchaseCheckout(cartData.items, token);

      if (!result.success) {
        throw new Error(result.error || 'Failed to create checkout session');
      }

      // Success - user will be redirected to Stripe Checkout
      // Cart will be cleared on successful payment via webhook
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  /**
   * Handle quantity change
   */
  const handleQuantityChange = (listingId, size, color, newQuantity) => {
    const qty = parseInt(newQuantity);
    if (isNaN(qty) || qty < 0) return;

    if (qty === 0) {
      removeItem(listingId, size, color);
    } else {
      updateQuantity(listingId, size, color, qty);
    }
  };

  /**
   * Handle clear cart
   */
  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      clearCart();
      setError('');
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="cart-backdrop" onClick={onClose} />

      {/* Cart Drawer */}
      <div className="cart-drawer">
        {/* Header */}
        <div className="cart-header">
          <div className="cart-header-title">
            <MaterialIcon icon="shopping_cart" />
            <h2>Your Cart</h2>
            <span className="cart-badge">{itemCount}</span>
          </div>
          <button className="cart-close-button" onClick={onClose} aria-label="Close cart">
            <MaterialIcon icon="close" />
          </button>
        </div>

        {/* Buy Direct Badge */}
        <div className="cart-buy-direct-badge">
          <MaterialIcon icon="storefront" />
          <span>Buy Direct - Help Us Save on Etsy Fees!</span>
        </div>

        {/* Cart Content */}
        <div className="cart-content">
          {isEmpty ? (
            /* Empty Cart */
            <div className="cart-empty">
              <MaterialIcon icon="shopping_cart" />
              <h3>Your cart is empty</h3>
              <p>Add stickers to your cart to checkout directly on ClothedEye.com</p>
              <p className="cart-empty-note">
                💡 Tip: Use the "Add to Cart" button on any sticker to buy direct!
              </p>
            </div>
          ) : (
            /* Cart Items */
            <>
              {/* Clear Cart Button */}
              <div className="cart-actions-top">
                <button className="cart-clear-button" onClick={handleClearCart}>
                  <MaterialIcon icon="delete_sweep" />
                  Clear Cart
                </button>
              </div>

              {/* Items List */}
              <div className="cart-items">
                {items.map((item) => {
                  const itemKey = `${item.listingId}-${item.size}${item.color ? `-${item.color}` : ''}`;

                  return (
                    <div key={itemKey} className="cart-item">
                      {/* Item Image */}
                      <div className="cart-item-image">
                        <img src={item.imageUrl} alt={item.title} />
                        {item.isSubscriberOnly && (
                          <div className="cart-item-subscriber-badge">
                            <MaterialIcon icon="verified" />
                          </div>
                        )}
                      </div>

                      {/* Item Details */}
                      <div className="cart-item-details">
                        <h4 className="cart-item-title">{item.title}</h4>
                        <div className="cart-item-meta">
                          <span className="cart-item-size">Size: {item.size}</span>
                          {item.color && (
                            <span className="cart-item-color">Color: {item.color}</span>
                          )}
                        </div>
                        <div className="cart-item-price">${item.price.toFixed(2)} each</div>

                        {/* Quantity Controls */}
                        <div className="cart-item-quantity">
                          <button
                            className="cart-qty-button"
                            onClick={() =>
                              handleQuantityChange(
                                item.listingId,
                                item.size,
                                item.color,
                                item.quantity - 1
                              )
                            }
                            aria-label="Decrease quantity"
                          >
                            <MaterialIcon icon="remove" />
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              handleQuantityChange(
                                item.listingId,
                                item.size,
                                item.color,
                                e.target.value
                              )
                            }
                            className="cart-qty-input"
                          />
                          <button
                            className="cart-qty-button"
                            onClick={() =>
                              handleQuantityChange(
                                item.listingId,
                                item.size,
                                item.color,
                                item.quantity + 1
                              )
                            }
                            aria-label="Increase quantity"
                          >
                            <MaterialIcon icon="add" />
                          </button>
                        </div>

                        {/* Item Total */}
                        <div className="cart-item-total">
                          ${(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <button
                        className="cart-item-remove"
                        onClick={() => removeItem(item.listingId, item.size, item.color)}
                        aria-label="Remove item"
                      >
                        <MaterialIcon icon="close" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Cart Summary */}
              <div className="cart-summary">
                <div className="cart-summary-row">
                  <span>Subtotal ({itemCount} items)</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="cart-summary-row">
                  <span>Estimated Shipping</span>
                  <span>${shipping.toFixed(2)}</span>
                </div>
                <div className="cart-summary-row cart-summary-total">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
                <p className="cart-summary-note">Tax calculated at checkout</p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="cart-error">
                  <MaterialIcon icon="error" />
                  <span>{error}</span>
                </div>
              )}

              {/* Checkout Button */}
              <button
                className="cart-checkout-button"
                onClick={handleCheckout}
                disabled={loading || isEmpty}
              >
                {loading ? (
                  <>
                    <span className="button-spinner" />
                    Processing...
                  </>
                ) : (
                  <>
                    <MaterialIcon icon="lock" />
                    Secure Checkout
                  </>
                )}
              </button>

              {/* Auth Requirement */}
              {!isAuthenticated && (
                <p className="cart-auth-note">
                  <MaterialIcon icon="info" />
                  You'll need to log in to complete your purchase
                </p>
              )}

              {/* Secure Badge */}
              <div className="cart-secure-badge">
                <MaterialIcon icon="verified_user" />
                <span>Secured by Stripe - Your payment info is safe</span>
              </div>
            </>
          )}
        </div>

        {/* Footer Info */}
        <div className="cart-footer">
          <p className="cart-footer-distinction">
            <MaterialIcon icon="info" />
            <strong>Direct Purchase Cart:</strong> Buy directly from ClothedEye.com
          </p>
          <p className="cart-footer-note">
            Looking for the Scene Builder Shopping List? That's for bulk Etsy orders and shows up in
            the Scene Builder sidebar.
          </p>
        </div>
      </div>
    </>
  );
};

export default ShoppingCart;
